// DASHBOARD — Section 10. Main hub screen.
// On appear: retries pending uploads, refreshes content cache if stale.
// "Begin Session" is only disabled while a session is actively in progress
// -- no time-based cooldown between sessions. Participants are trusted to
// pace themselves; reminders (push notifications) are what keep them on
// schedule, not a lockout.

import { useEffect, useState, useCallback } from 'react';
import { useParticipantStore } from '../store/participantStore';
import { useSessionStore } from '../store/sessionStore';
import { retryPendingUploads } from '../upload/uploadManager';
import { ensureContentCached } from '../utils/content';
import { countPendingUploads } from '../db/indexedDB';
import { nextSessionTime, formatTime } from '../utils/schedule';
import UploadStatus from '../components/UploadStatus';

const BUILD_ID = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

export default function DashboardScreen({ onBeginSession, onOpenResearcherPanel, onOpenDiagnostics }) {
  const participant = useParticipantStore();
  const startSession = useSessionStore((s) => s.startSession);
  const [pendingCount, setPendingCount] = useState(0);
  const [titleHoldStart, setTitleHoldStart] = useState(null);

  const refresh = useCallback(async () => {
    setPendingCount(await countPendingUploads());
    retryPendingUploads().then(() => countPendingUploads().then(setPendingCount));
    ensureContentCached().catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const now = Date.now();
  const canBegin = !participant.active_in_session;

  const next = nextSessionTime(participant.schedule, new Date(now));

  async function handleBegin() {
    const sessionId = crypto.randomUUID();
    const totalSessions = Number(import.meta.env.VITE_TOTAL_SESSIONS ?? 35);
    const dayNumber = Math.floor(participant.session_number / 5) + 1;
    const sessionWithinDay = (participant.session_number % 5) + 1;

    await participant.patch({ active_in_session: true, current_session_id: sessionId });
    startSession({
      sessionId,
      participantId: participant.participant_id,
      sessionNumber: participant.session_number + 1,
      contentIndex: participant.content_index % totalSessions,
      dayNumber,
      sessionWithinDay,
    });
    onBeginSession();
  }

  // Long-press (5s) on the title opens the researcher panel — spec Section 10.
  // Only checks elapsed hold time on release; deliberately does NOT cancel
  // on pointer movement/leave — a mouse drifting a few pixels during a
  // 5-second hold is normal and shouldn't kill the gesture (this was
  // cancelling real attempts on desktop before this fix).
  // -webkit-touch-callout:none (below, via inline style since Tailwind has
  // no built-in utility for it) stops iOS Safari's native "Copy / Look Up"
  // popup from hijacking the hold before the 5 seconds are up — select-none
  // alone (user-select) does NOT suppress that callout, only text selection.
  function handleTitleDown() {
    setTitleHoldStart(Date.now());
  }
  function handleTitleUp() {
    if (titleHoldStart && Date.now() - titleHoldStart >= 5000) onOpenResearcherPanel();
    setTitleHoldStart(null);
  }

  return (
    <div className="screen relative">
      {/* Small, deliberately low-key gear icon rather than a labeled
          "Researcher Panel" button -- this still requires the same PIN to
          do anything, so a curious participant tap just shows a PIN screen
          and nothing else, but it IS meant to be easy for you to find
          during setup/testing (unlike a hidden long-press). Long-press on
          the title below still works too, as a second path. */}
      <button
        type="button"
        onClick={onOpenResearcherPanel}
        aria-label="Researcher settings"
        className="absolute top-4 right-4 min-h-touch min-w-[44px] flex items-center justify-center text-dim text-xl opacity-50"
      >
        ⚙
      </button>

      <div className="text-center mb-8 select-none">
        <h1
          className="font-display text-4xl tracking-tight"
          style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
          onPointerDown={handleTitleDown}
          onPointerUp={handleTitleUp}
          onContextMenu={(e) => e.preventDefault()}
        >
          ASPIRE
        </h1>
        {/* Which participant this device is currently set up for. Without it
            there was no way to confirm an ID change in the researcher panel
            had taken effect — or to notice a device left on the wrong ID. */}
        {participant.participant_id && (
          <p className="text-dim/60 text-xs mt-1 tracking-wide">ID {participant.participant_id}</p>
        )}
      </div>

      <div className="card mb-4 text-center">
        <p className="text-dim text-sm mb-1">Up next</p>
        <p className="font-display text-2xl">Session {participant.session_number + 1}</p>
        {next && (
          <p className="text-dim text-sm mt-2">
            Next reminder around {formatTime(next.date.getHours(), next.date.getMinutes())}
          </p>
        )}
      </div>

      <div className="mb-8">
        <UploadStatus pendingCount={pendingCount} />
      </div>

      <button type="button" disabled={!canBegin} onClick={handleBegin} className="btn-primary">
        Begin Session
      </button>
      {participant.active_in_session && (
        <div className="text-center mt-3">
          <p className="text-dim text-sm">A session is already in progress.</p>
          <button
            type="button"
            onClick={() => participant.patch({ active_in_session: false, current_session_id: null })}
            className="text-amber text-sm underline mt-1"
          >
            Something went wrong — reset and try again
          </button>
        </div>
      )}

      {/* Build stamp doubling as the diagnostics entry point.
          Two jobs, both about the Android freeze: it makes "is this phone
          actually running the new build?" answerable at a glance instead of
          by inference (service workers cache hard, and an installed PWA can
          run a build behind for a launch or two), and it's how you get to the
          crash log after force-quitting a frozen session. Small and unlabeled
          enough that a participant won't be drawn to it. */}
      <button
        type="button"
        onClick={onOpenDiagnostics}
        className="absolute bottom-2 left-0 right-0 mx-auto text-[10px] text-dim/40"
      >
        build {BUILD_ID}
      </button>
    </div>
  );
}
